import React, { useEffect, useMemo, useState } from 'react'
import { DataGrid } from '@mui/x-data-grid'
import { Box, Container, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { convertToUserDateFormate } from 'utils/helpers'
import { useSelector } from 'react-redux'
import { height } from '@fortawesome/free-solid-svg-icons/faInfo'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

function ListDocuments({ fetchDocuments, documents, setSelectedRows }) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const siteUser = useSelector((store) => store.user)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [rows, setRows] = useState(20)
  const [page, setPage] = useState(0)
  const [modifiedColumns, setModifiedColumns] = useState([])

  const navigate = useNavigate()

  const handleRowsChange = (newPageSize) => {
    setRows(newPageSize)
    let url = `&limit=${newPageSize}&offset=${page}`
    fetchDocuments(url)
  }
  const handlePageChange = (newPage) => {
    setPage(newPage * rows)
    let url = `&limit=${rows}&offset=${newPage * rows}`
    fetchDocuments(url)
  }
  // LAP-733 (GE022) — memoized columns so headerName re-evaluates on locale change
  const columns = useMemo(
    () => [
      { field: 'id', headerName: t('realEstate.headerID'), width: 90 },
      {
        field: 'nom',
        headerName: t('realEstate.headerName'),
        width: 150,
        editable: true,
      },
      {
        field: 'pays',
        headerName: t('realEstate.headerCountry'),
        width: 100,
        editable: true,
        valueGetter: (params) => `${params.row.payss.Designation}`,
      },
      {
        field: 'utilisation',
        headerName: t('realEstate.headerUsage'),
        width: 170,
        editable: true,
      },
      {
        field: 'created_at',
        headerName: t('realEstate.headerCreatedAt'),
        width: 150,
        editable: false,
        valueGetter: (params) => convertToUserDateFormate(siteUser.user, params.row.created_at),
      },
      {
        field: 'pieces',
        headerName: t('realEstate.headerRooms'),
        width: 150,
        editable: true,
      },
      {
        field: 'superficie',
        headerName: t('realEstate.headerSurface'),
        width: 150,
        valueGetter: (params) => `${params.row.superficies.answer}`,
      },
      {
        field: 'consumption',
        headerName: t('realEstate.headerEnergyConsumption'),
        sortable: false,
        width: 220,
        valueGetter: (params) => `${params.row.consumptions.answer}`,
      },
      {
        field: 'energy_classification',
        headerName: t('realEstate.headerEnergyClass'),
        sortable: false,
        width: 400,
        valueGetter: (params) => `${params.row.energy_classifications.answer}`,
      },
      {
        field: 'type_de_bien',
        headerName: t('realEstate.headerPropertyType'),
        sortable: false,
        width: 150,
        valueGetter: (params) => `${params.row.type_de_biens.answer}`,
      },
    ],
    [t, siteUser.user],
  )

  useEffect(() => {
    if (isMobile) {
      setModifiedColumns([
        {
          field: 'Nom',
          headerName: t('realEstate.headerName'),
          width: 280,
          // valueGetter: (params) => `${params.row.Nom?.Nom || params.row.IBAN}`,
          renderCell: (params) => (
            <Box py={2} mt={1}>
              <Box fontWeight={600}>{params.row.nom}</Box>
              <Box>
                {/* LAP-733 (GE022) */}
                <Box component={'span'}>{t('realEstate.headerUsage')}:</Box>{' '}
                <Box component={'span'} fontWeight={600}>
                  {params.row.utilisation}
                </Box>
              </Box>
              <Box>
                {/* LAP-733 (GE022) */}
                <Box component={'span'}>{t('realEstate.headerCreatedAt')}:</Box>{' '}
                <Box component={'span'} fontWeight={600}>
                  {convertToUserDateFormate(siteUser.user, params.row.created_at)}
                </Box>
              </Box>
              <Box>
                {/* LAP-733 (GE022) */}
                <Box component={'span'}>{t('realEstate.headerRooms')}:</Box>{' '}
                <Box component={'span'} fontWeight={600}>
                  {params.row.pieces}
                </Box>
              </Box>
              <Box>
                {/* LAP-733 (GE022) */}
                <Box component={'span'}>{t('realEstate.headerSurface')}:</Box>{' '}
                <Box component={'span'} fontWeight={600}>
                  {params.row.superficies.answer}
                </Box>
              </Box>
              <Box>
                {/* LAP-733 (GE022) */}
                <Box component={'span'}>{t('realEstate.headerEnergyConsumption')}:</Box>{' '}
                <Box component={'span'} fontWeight={600}>
                  {params.row.consumptions.answer}
                </Box>
              </Box>
              <Box>
                {/* LAP-733 (GE022) */}
                <Box component={'span'}>{t('realEstate.headerEnergyClass')}:</Box>{' '}
                <Box component={'span'} fontWeight={600}>
                  {params.row.energy_classifications.answer}
                </Box>
              </Box>
            </Box>
          ),
        },
      ])
    } else {
      setModifiedColumns(columns)
    }
  }, [isMobile, columns, t, siteUser.user])
  return (
    <Box
      style={{
        width: '100%',
        background: 'white',
        borderRadius: '5px',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          flex: '1 1 100%',
          id: 'tableTitle',
          fontWeight: 600,
          padding: '10px',
          component: 'div',
        }}
      >
        {/* LAP-733 (GE022) */}
        {t('realEstate.pageTitle')} ({documents.count})
      </Typography>
      <DataGrid
        sx={{ height: 700 }}
        rows={documents.results ? documents.results : []}
        columns={modifiedColumns}
        getRowHeight={() => (isMobile ? 'auto' : 50)}
        // autoHeight
        pageSize={rows}
        rowCount={documents.count ? documents.count : 0}
        onPageSizeChange={handleRowsChange}
        onRowClick={(e) => navigate(`monbien/${e.row.id}`)}
        onPageChange={handlePageChange}
        rowsPerPageOptions={[10, 20, 40]}
        pagination
        onSelectionModelChange={(itm) => setSelectedRows([...itm])}
        paginationMode="server"
        checkboxSelection
      />
    </Box>
  )
}
export default ListDocuments
